import React, { useCallback, useEffect, useState } from 'react'
import Stack from '@mui/material/Stack'
import { useLayoutConstants } from '../hooks/useLayoutConstants'
import TPPCellUI from '../../../views/TPP/components/TPPCell/TPPCellUI'

type TppContextCellProps = {
    slotId: string
    context?: string
    onUpdateContext: (slotId: string, context: string) => Promise<void>
    canEdit?: boolean
}

const TppContextCell: React.FC<TppContextCellProps> = ({ slotId, context, onUpdateContext, canEdit = true }) => {
    const layout = useLayoutConstants()
    const CONTENT_MAX_HEIGHT = 350
    const [isEditing, setIsEditing] = useState(false)
    const [draft, setDraft] = useState(context || '')
    const [isSaving, setIsSaving] = useState(false)

    useEffect(() => {
        if (!isEditing) {
            setDraft(context || '')
        }
    }, [context, isEditing])

    const commit = useCallback(async () => {
        const nextRaw = draft
        const prev = context || ''
        const next = nextRaw.trim()

        setIsEditing(false)

        if (next === prev) return

        setIsSaving(true)
        try {
            await onUpdateContext(slotId, next)
        } finally {
            setIsSaving(false)
        }
    }, [context, draft, onUpdateContext, slotId])

    const cancel = useCallback(() => {
        setDraft(context || '')
        setIsEditing(false)
    }, [context])

    const hasContent = (context || '').trim().length > 0
    const displayText = hasContent ? context : 'Add note'

    return (
        <Stack
            direction="column"
            p={1.5}
            sx={{
                flex: '0 0 auto',
                width: layout.COLUMN_WIDTH,
                minHeight: 132.75,
                borderRight: '1px solid',
                borderColor: 'figma.grayscale.stroke',
                bgcolor: 'figma.grayscale.contentWrapper',
                position: 'sticky',
                left: layout.SCROLL_START,
                zIndex: 25,
                cursor: isSaving ? 'progress' : canEdit ? 'text' : 'default',
            }}
            onClick={() => {
                if (isEditing || isSaving || !canEdit) return
                setIsEditing(true)
            }}
        >
            <TPPCellUI
                isEditing={isEditing}
                draftContent={draft}
                onDraftChange={setDraft}
                onCommit={commit}
                onCancel={cancel}
                onEnterEdit={() => {
                    if (isSaving || !canEdit) return
                    setIsEditing(true)
                }}
                displayText={displayText}
                hasDisplayContent={hasContent}
                isReadOnly={!canEdit}
                isMovePending={false}
                contentMaxHeight={CONTENT_MAX_HEIGHT}
                backgroundColor="figma.grayscale.contentWrapper"
                borderColor="figma.grayscale.stroke"
                borderWidth={1}
                textColor="figma.grayscale.textHier2"
                placeholderColor="figma.grayscale.tier4"
                showFooter={false}
            />
        </Stack>
    )
}

export default TppContextCell
